/* ============================================================
   DULCE PILATES — Interactions
   ============================================================ */

/* ── Navigation scroll state ─────────────────────────────── */
const nav = document.getElementById('nav');

// Keep header spacing accurate when the promotion wraps or text is enlarged.
let promotionObserver;
const syncPromotionBar = () => {
  promotionObserver?.disconnect();
  document.documentElement.style.removeProperty('--promotion-bar-height');
  const bar = document.querySelector('.promotion-bar');
  if (!bar) return;
  promotionObserver = new ResizeObserver(() => {
    document.documentElement.style.setProperty('--promotion-bar-height', `${bar.getBoundingClientRect().height}px`);
    const currentNav = document.getElementById('nav');
    currentNav.style.setProperty('--nav-bottom', `${currentNav.getBoundingClientRect().bottom}px`);
  });
  promotionObserver.observe(bar);
};
syncPromotionBar();
document.addEventListener('shopify:section:load', syncPromotionBar);

const onScroll = () => {
  nav.classList.toggle('scrolled', window.scrollY > 60);
};

window.addEventListener('scroll', onScroll, { passive: true });
onScroll();

/* ── Responsive navigation ───────────────────────────────── */
const burger = document.getElementById('burger');
const navLinks = document.querySelector('.nav__links');

if (burger && navLinks) {
  const compactNav = window.matchMedia('(max-width: 1100px)');
  const burgerSpans = burger.querySelectorAll('span');
  const links = [...navLinks.querySelectorAll('a')];
  let inactiveBranches = [];

  const updateMenuHeight = () => {
    nav.style.setProperty('--nav-bottom', `${nav.getBoundingClientRect().bottom}px`);
  };

  const setMenuOpen = (open, restoreFocus = false) => {
    open = open && compactNav.matches;
    const wasOpen = navLinks.classList.contains('open');
    if (open === wasOpen) return;
    navLinks.classList.toggle('open', open);
    burger.setAttribute('aria-expanded', String(open));
    burger.setAttribute('aria-label', open ? 'Fermer le menu' : 'Menu');
    document.body.classList.toggle('nav-open', open);
    burgerSpans[0].style.transform = open ? 'rotate(45deg) translate(4px, 4px)' : '';
    burgerSpans[1].style.transform = open ? 'rotate(-45deg) translate(4px, -4px)' : '';

    if (open) {
      updateMenuHeight();
      nav.setAttribute('role', 'dialog');
      nav.setAttribute('aria-modal', 'true');
      // Also supports Shopify's section wrapper around the header.
      for (let branch = nav; branch.parentElement; branch = branch.parentElement) {
        for (const sibling of branch.parentElement.children) {
          if (sibling !== branch && !sibling.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
            sibling.inert = true;
            inactiveBranches.push(sibling);
          }
        }
        if (branch.parentElement === document.body) break;
      }
      links[0]?.focus({ preventScroll: true });
    } else {
      nav.removeAttribute('role');
      nav.removeAttribute('aria-modal');
      inactiveBranches.forEach(element => { element.inert = false; });
      inactiveBranches = [];
      if (restoreFocus) {
        (compactNav.matches ? burger : links[0])?.focus({ preventScroll: true });
      }
    }
  };

  burger.addEventListener('click', () => setMenuOpen(!navLinks.classList.contains('open'), true));
  links.forEach(link => link.addEventListener('click', () => setMenuOpen(false, true)));

  nav.addEventListener('keydown', event => {
    if (!navLinks.classList.contains('open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setMenuOpen(false, true);
    } else if (event.key === 'Tab') {
      const controls = [...nav.querySelectorAll('a[href], button:not([disabled])')];
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
  });

  compactNav.addEventListener('change', () => {
    if (!compactNav.matches) setMenuOpen(false, true);
  });
  window.addEventListener('resize', () => {
    if (navLinks.classList.contains('open')) updateMenuHeight();
  }, { passive: true });

  // Match both local .html routes and Shopify page handles.
  const normalizePath = path => path.replace(/\/index\.html$/, '/').replace(/\.html$/, '').replace(/\/$/, '') || '/';
  const markCurrentLink = () => links.forEach(link => {
    const target = new URL(link.href, window.location.href);
    const samePage = target.origin === window.location.origin && normalizePath(target.pathname) === normalizePath(window.location.pathname);
    const current = samePage && (target.hash ? target.hash === window.location.hash : true);
    if (current) link.setAttribute('aria-current', target.hash ? 'location' : 'page');
    else link.removeAttribute('aria-current');
  });
  markCurrentLink();
  window.addEventListener('hashchange', markCurrentLink);
}

/* ── Scroll reveal ────────────────────────────────────────── */
const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry, i) => {
      if (entry.isIntersecting) {
        // stagger siblings in the same parent
        const siblings = [...entry.target.parentElement.querySelectorAll('.reveal')];
        const idx = siblings.indexOf(entry.target);
        entry.target.style.transitionDelay = `${idx * 0.1}s`;
        entry.target.classList.add('visible');
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
);

document.querySelectorAll('.reveal').forEach(el => {
  // Content stays visible if JavaScript fails or is unavailable.
  el.classList.add('reveal-ready');
  revealObserver.observe(el);
});

/* ── FAQ accordion — close others on open ─────────────────── */
document.querySelectorAll('.faq__item').forEach(item => {
  item.addEventListener('toggle', () => {
    if (item.open) {
      document.querySelectorAll('.faq__item').forEach(other => {
        if (other !== item) other.removeAttribute('open');
      });
    }
  });
});

/* ── Scroll progress bar ──────────────────────────────────── */
const progressBar = document.getElementById('progressBar');

if (progressBar) {
  window.addEventListener('scroll', () => {
    const scrolled = window.scrollY;
    const total = document.documentElement.scrollHeight - window.innerHeight;
    progressBar.style.width = `${(scrolled / total) * 100}%`;
  }, { passive: true });
}

/* ── Custom cursor ────────────────────────────────────────── */
const cursorDot = document.getElementById('cursorDot');

if (cursorDot && window.matchMedia('(pointer: fine)').matches) {
  document.documentElement.classList.add('has-custom-cursor');

  let curX = 0, curY = 0;
  let dotX = 0, dotY = 0;

  window.addEventListener('mousemove', e => {
    curX = e.clientX;
    curY = e.clientY;
  });

  const animateCursor = () => {
    dotX += (curX - dotX) * 0.28;
    dotY += (curY - dotY) * 0.28;
    cursorDot.style.left = `${dotX}px`;
    cursorDot.style.top  = `${dotY}px`;
    requestAnimationFrame(animateCursor);
  };
  animateCursor();

  document.querySelectorAll('a, button, .service-card, .faq__question').forEach(el => {
    el.addEventListener('mouseenter', () => cursorDot.classList.add('hovering'));
    el.addEventListener('mouseleave', () => cursorDot.classList.remove('hovering'));
  });
}

/* ── Sticky booking pill ──────────────────────────────────── */
const stickyPill = document.getElementById('stickyPill');

if (stickyPill) {
  const showPillObserver = new IntersectionObserver(
    ([entry]) => {
      stickyPill.classList.toggle('visible', !entry.isIntersecting);
    },
    { threshold: 0 }
  );
  const hero = document.querySelector('.hero');
  if (hero) showPillObserver.observe(hero);
}

/* ── Schedule view toggle (Horaire page) ──────────────────── */
const scheduleToggle = document.querySelector('.schedule-toggle');
if (scheduleToggle) {
  const iframe = document.querySelector('.schedule-frame iframe');
  const srcCalendar = iframe?.dataset.srcCalendar;
  const srcList = iframe?.dataset.srcList;
  const setScheduleView = (view) => {
    scheduleToggle.querySelectorAll('button').forEach(button => {
      const active = button.dataset.view === view;
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', active);
    });

    if (!iframe) return;
    const target = view === 'list' ? srcList : srcCalendar;
    if (target && iframe.src !== target) iframe.src = target;
  };

  scheduleToggle.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('click', () => {
      setScheduleView(btn.dataset.view || 'calendar');
    });
  });

  if (window.matchMedia('(max-width: 760px)').matches) {
    setScheduleView('list');
  }

  /* Mark frame as loaded to fade out shimmer skeleton */
  if (iframe) {
    const scheduleFrame = document.querySelector('.schedule-frame');
    iframe.addEventListener('load', () => scheduleFrame?.classList.add('loaded'));
  }

  /* Auto-resize: listen for postMessage height from Fliip embed */
  window.addEventListener('message', (e) => {
    if (!iframe) return;
    const d = e.data;
    if (typeof d === 'object' && d !== null && (d.height || d.frameHeight)) {
      const h = parseInt(d.height || d.frameHeight, 10);
      if (!isNaN(h) && h > 200) iframe.style.height = h + 'px';
    }
    if (typeof d === 'number' && d > 200) {
      iframe.style.height = d + 'px';
    }
  });
}

/* ── Axeptio cookie consent ───────────────────────────────── */
(() => {
  window._axcb = window._axcb || [];

  document.querySelectorAll('[data-axeptio-preferences]').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      window._axcb.push(sdk => sdk.openCookies());
    });
  });

})();

/* ── Magnetic buttons ─────────────────────────────────────── */
if (window.matchMedia('(pointer: fine)').matches) {
  document.querySelectorAll('.btn').forEach(btn => {
    btn.addEventListener('mousemove', e => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      btn.style.transform = `translate(${x * 0.2}px, ${y * 0.3}px) translateY(-2px)`;
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
    });
  });
}

/* ── Hero film ────────────────────────────────────────────── */
const heroVideo = document.querySelector('.hero__video');
if (heroVideo) {
  const autoPlayback = window.matchMedia('(prefers-reduced-motion: no-preference) and (min-width: 768px)');
  const updateHeroPlayback = () => {
    // Mobile visitors load the film when they press Play, saving the initial download.
    heroVideo.autoplay = autoPlayback.matches;
    if (!autoPlayback.matches) heroVideo.pause();
    else heroVideo.play().catch(() => {}); // Native controls remain available if autoplay is blocked.
  };
  autoPlayback.addEventListener('change', updateHeroPlayback);
  updateHeroPlayback();
}

/* ── Sauna / cold bath scroll tour ────────────────────────── */
const wellnessStory = document.querySelector('[data-wellness-story]');
if (wellnessStory) {
  const motion = window.matchMedia('(prefers-reduced-motion: no-preference) and (min-height: 600px)');
  const stage = wellnessStory.querySelector('.wellness-story__stage');
  const controls = wellnessStory.querySelector('.wellness-story__controls');
  const steps = [...wellnessStory.querySelectorAll('[data-wellness-step]')];
  let frame = 0;

  const geometry = () => ({
    top: parseFloat(getComputedStyle(stage).top),
    rect: wellnessStory.getBoundingClientRect(),
    range: Math.max(1, wellnessStory.offsetHeight - stage.offsetHeight),
  });
  const update = () => {
    frame = 0;
    if (!motion.matches) return;
    const { top, rect, range } = geometry();
    const progress = Math.max(0, Math.min(1, (top - rect.top) / range));
    // Hold each scene briefly before and after the horizontal transition.
    const travel = Math.max(0, Math.min(1, (progress - .15) / .7));
    wellnessStory.style.setProperty('--wellness-progress', travel);
    steps.forEach((step, index) => step.setAttribute('aria-pressed', String(index === (travel < .5 ? 0 : 1))));
  };
  const schedule = () => {
    if (!frame && motion.matches) frame = requestAnimationFrame(update);
  };
  const configure = () => {
    wellnessStory.classList.toggle('is-enhanced', motion.matches);
    controls.hidden = !motion.matches;
    if (motion.matches) window.addEventListener('scroll', schedule, { passive: true });
    else window.removeEventListener('scroll', schedule);
    schedule();
  };
  steps.forEach((step, index) => step.addEventListener('click', () => {
    const { top, rect, range } = geometry();
    window.scrollTo({ top: window.scrollY + rect.top - top + range * index, behavior: 'smooth' });
  }));
  motion.addEventListener('change', configure);
  window.addEventListener('resize', schedule, { passive: true });
  configure();
}
